/**
 * Records what an order's postage actually COSTS the shop, independent of what
 * the customer was charged.
 *
 * Why this exists: when a cart crosses the free-shipping threshold,
 * `calculateShipping()` returns 0 and that zero is what lands on the order. The
 * carrier still gets paid. Without this snapshot the absorbed amount is simply
 * lost, and the monthly settlement silently overstates the margin the partners
 * split. Same story when postage is waived by hand below the threshold.
 *
 * Computed server-side at order creation from the cart the server already
 * loaded — never taken from the client, since it feeds a money calculation.
 */

import { hasManualDeliveryItems } from './manualDeliveryDetector'
import { getBaseShippingCost, getShippingCountry, resolveItemWeight } from './shippingCalculator'

/** Total cart weight in kg. Mirrors the cart provider's `cartWeightKg`. */
export function computeCartWeightKg(items: unknown[]): number {
  if (!Array.isArray(items)) return 0
  return items.reduce<number>((total, raw) => {
    const item = raw as { product?: unknown; variant?: unknown; quantity?: number }
    const productWeight =
      item.product && typeof item.product === 'object'
        ? ((item.product as { weightKg?: number | null }).weightKg ?? null)
        : null
    const variantWeight =
      item.variant && typeof item.variant === 'object'
        ? ((item.variant as { weightKg?: number | null }).weightKg ?? null)
        : null
    const weight = resolveItemWeight(productWeight, variantWeight)
    return total + (weight ?? 0) * (item.quantity || 1)
  }, 0)
}

/**
 * The carrier cost of this cart in cents, ignoring free shipping and waivers.
 *
 * Returns 0 for manual-delivery orders: those are hand-delivered or arranged
 * separately, so there is no parcel tariff for the shop to absorb.
 */
export function computeBaseShippingCostCents(
  items: unknown[],
  countryCode?: string | null,
): number {
  if (!Array.isArray(items) || items.length === 0) return 0
  if (hasManualDeliveryItems(items as never[])) return 0

  const weightKg = computeCartWeightKg(items)
  return getBaseShippingCost(getShippingCountry(countryCode ?? undefined), weightKg || null)
}
